import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaTrash, FaEllipsisV } from 'react-icons/fa';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { fromZonedTime } from 'date-fns-tz';

import './HorseCard.css';
import supabase from '../../../config/supabaseClient';
import { useAuth } from '../../../auth/AuthProvider';
import { useSubscription } from '../../../hooks/useSubscription';

// Matches the options in RaceInformation's timezone dropdown.
const RACE_TIMEZONE_LABELS = {
  'America/Los_Angeles': { short: 'PT', full: 'Pacific' },
  'America/Denver': { short: 'MT', full: 'Mountain' },
  'America/Chicago': { short: 'CT', full: 'Central' },
  'America/New_York': { short: 'ET', full: 'Eastern' },
};

const HorseCard = ({
  horse,
  onEdit,
  onDelete,
  onClone,
  onSendSummary,
  colors,
  commonStyles,
  reminderEnabled = false,
  onToggleReminder,
}) => {
  const { user } = useAuth();
  const { isActive: isSubActive } = useSubscription();
  const [showDropdown, setShowDropdown] = useState(false);
  const menuRef = useRef(null);

  // Loading state for reminder operations (currently set but not displayed in UI)
  // eslint-disable-next-line no-unused-vars
  const [reminderLoading, setReminderLoading] = useState(false);

  // null = off, number = lead time in minutes
  const [reminderSettings, setReminderSettings] = useState({
    tool1: null,
    tool2: null,
    tool3: null,
  });

  // User's timezone (auto-detected and stored in profile) — used as a fallback
  // for horses saved before the explicit race timezone field existed.
  const [userTimezone, setUserTimezone] = useState(null);

  // The timezone reminders should actually be scheduled against: the explicit
  // per-race timezone the user picked on the horse form, if set, otherwise the
  // device-detected fallback. Explicit selection avoids relying on whichever
  // device happens to load the app (which is unreliable for laptops/shared
  // devices, and doesn't reflect where the race itself is happening).
  const effectiveTimezone = horse?.race_timezone || userTimezone;

  // SMS opt-out source (to detect STOP opt-out)
  const [smsOptOutSource, setSmsOptOutSource] = useState('');

  // Track if SMS is enabled globally (has phone number and sms_opt_in)
  const [smsEnabledGlobally, setSmsEnabledGlobally] = useState(true); // Default to true to avoid flicker

  // Track phone number and SMS opt-in separately for better tooltip messages
  const [hasPhoneNumber, setHasPhoneNumber] = useState(false);
  const [hasSmsOptIn, setHasSmsOptIn] = useState(false);

  // Current time state - updates every minute to trigger re-renders for time-based checks
  // This ensures reminder status updates automatically when time passes without page refresh
  const [currentTime, setCurrentTime] = useState(new Date());

  // Is the race date already in the past?
  const raceDateStr = horse?.race_date;
  const hasRaceDate = Boolean(raceDateStr);

  const isRaceInPast = (() => {
    if (!raceDateStr) return false;
    try {
      // Normalize to YYYY-MM-DD if needed (handle MM/DD/YYYY)
      let dateStr = raceDateStr;
      if (raceDateStr.includes('/')) {
        const [month, day, year] = raceDateStr.split('/');
        dateStr = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }

      const [yearStr, monthStr, dayStr] = dateStr.split('-');
      const year = Number(yearStr);
      const month = Number(monthStr);
      const day = Number(dayStr);

      if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
        console.warn('Invalid race date string:', raceDateStr);
        return false;
      }

      // Race date at local midnight
      const raceDateOnly = new Date(year, month - 1, day);

      // Today's date at local midnight
      const now = new Date();
      const todayDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      // Consider race "past" only if its calendar date is before today.
      // This allows same-day therapeutic reminders even after race_start_time.
      return raceDateOnly < todayDateOnly;
    } catch (err) {
      console.error('Error checking if race is in past (date-only):', err);
      return false;
    }
  })();

  // Check if race time has passed (for same-day races)
  const isRaceTimePassed = (() => {
    if (!raceDateStr || !horse?.race_start_time || horse.race_start_time === 'N/A') return false;

    try {
      // Check if race date is today
      let dateStr = raceDateStr;
      if (raceDateStr.includes('/')) {
        const [month, day, year] = raceDateStr.split('/');
        dateStr = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }

      const [yearStr, monthStr, dayStr] = dateStr.split('-');
      const year = Number(yearStr);
      const month = Number(monthStr);
      const day = Number(dayStr);

      if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
        return false;
      }

      const raceDateOnly = new Date(year, month - 1, day);
      const now = new Date();
      const todayDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      // Only check time if race date is today
      if (raceDateOnly.getTime() !== todayDateOnly.getTime()) {
        return false; // Not today, so time check doesn't apply
      }

      // Parse race start time (format: "HH:MM" or "HH:MM:SS")
      const timeStr = horse.race_start_time;
      const [hoursStr, minutesStr] = timeStr.split(':');
      const raceHour = Number(hoursStr);
      const raceMinute = Number(minutesStr);

      if (!Number.isFinite(raceHour) || !Number.isFinite(raceMinute)) {
        return false;
      }

      // Create race datetime (today at race time)
      const raceDateTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), raceHour, raceMinute, 0);

      // Check if race time has passed
      return now > raceDateTime;
    } catch (err) {
      console.error('Error checking if race time has passed:', err);
      return false;
    }
  })();

  // Check if there are any therapeutic times set
  const hasTherapeuticTimes = (() => {
    const therapeuticTimes = [1, 2, 3].map(num => horse?.[`time_to_give_tool${num}`] || 'N/A');
    const definedTimes = therapeuticTimes.filter(t => t !== 'N/A');
    return definedTimes.length > 0;
  })();

  // Check if ALL therapeutic times have passed (no valid reminder options for any of them)
  // NOTE: This must be defined BEFORE isLeadOptionValid since it uses inlined logic
  const allTherapeuticTimesPassed = (() => {
    if (!hasRaceDate || isRaceInPast) return isRaceInPast;

    // If no therapeutic times defined, return false (not "passed", just "not set")
    if (!hasTherapeuticTimes) return false;

    // If timezone not loaded yet, assume times haven't passed (safer default)
    if (!effectiveTimezone) return false;

    // Helper to calculate reminder date (inlined to avoid circular dependency)
    // Uses timezone-aware conversion - inlined here to avoid hoisting issues
    const calcReminderDate = (therapeuticTime, leadMinutes) => {
      if (!therapeuticTime || therapeuticTime === 'N/A' || !effectiveTimezone) return null;
      try {
        const [timePart, period] = therapeuticTime.split(' ');
        const [h, m] = timePart.split(':').map(Number);

        let hour24 = h;
        if (period === 'PM' && hour24 !== 12) hour24 += 12;
        if (period === 'AM' && hour24 === 12) hour24 = 0;

        const dateStr = raceDateStr ?? new Date().toISOString().slice(0, 10);

        // Build local datetime string in the race's timezone
        const localDateTime = `${dateStr} ${hour24
          .toString()
          .padStart(2, '0')}:${m.toString().padStart(2, '0')}:00`;

        // Convert from the race's timezone → UTC
        const utcDate = fromZonedTime(localDateTime, effectiveTimezone);

        // Subtract lead time in UTC (safe)
        utcDate.setMinutes(utcDate.getMinutes() - leadMinutes);

        return utcDate;
      } catch (err) {
        return null;
      }
    };

    // Helper to check if lead option is valid
    const checkLeadValid = (therapeuticTime, leadMinutes) => {
      const reminderDate = calcReminderDate(therapeuticTime, leadMinutes);
      if (!reminderDate) return false;
      // Use currentTime to ensure consistent time checks and automatic UI updates
      return reminderDate > currentTime;
    };

    const leadOptions = [5, 10, 15, 30, 60];
    const therapeuticTimes = [1, 2, 3].map(num => horse?.[`time_to_give_tool${num}`] || 'N/A');

    // Get only the defined therapeutic times (not 'N/A')
    const definedTimes = therapeuticTimes.filter(t => t !== 'N/A');

    // Check if ALL defined therapeutic times have ALL their lead options passed
    return definedTimes.every(time =>
      leadOptions.every(lead => !checkLeadValid(time, lead))
    );
  })();

  const isLeadOptionValid = useCallback(
    (therapeuticTime, leadMinutes) => {
      if (!hasRaceDate || isRaceInPast || !effectiveTimezone) return false;

      // Inline timezone conversion to avoid hoisting issues
      if (!therapeuticTime || therapeuticTime === 'N/A') return false;
      try {
        const [timePart, period] = therapeuticTime.split(' ');
        const [h, m] = timePart.split(':').map(Number);

        let hour24 = h;
        if (period === 'PM' && hour24 !== 12) hour24 += 12;
        if (period === 'AM' && hour24 === 12) hour24 = 0;

        const dateStr = raceDateStr ?? new Date().toISOString().slice(0, 10);

        // Build local datetime string in the race's timezone
        const localDateTime = `${dateStr} ${hour24
          .toString()
          .padStart(2, '0')}:${m.toString().padStart(2, '0')}:00`;

        // Convert from the race's timezone → UTC
        const utcDate = fromZonedTime(localDateTime, effectiveTimezone);

        // Subtract lead time in UTC (safe)
        utcDate.setMinutes(utcDate.getMinutes() - leadMinutes);

        return utcDate > new Date();
      } catch (err) {
        return false;
      }
    },
    [hasRaceDate, isRaceInPast, effectiveTimezone, raceDateStr]
  );

  /**
   * Load previous reminder settings from the database
   * This retrieves the most recent reminder (even if cancelled) for each tool
   * to preserve user's previous lead time preferences
   * Returns { found: boolean, hasAnyInteraction: boolean }
   * MUST be defined before useEffect that uses it to avoid lint error
   */
  const loadReminderSettingsFromDB = useCallback(async () => {
    if (!user?.id || !horse?.id) {
      return { found: false, hasAnyInteraction: false };
    }

    try {
      // First, verify user has phone number and SMS opt-in
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('phone_number, sms_opt_in')
        .eq('user_id', user.id)
        .single();

      if (profileError || !profile) {
        console.error('Failed to load profile:', profileError);
        return { found: false, hasAnyInteraction: false, allNull: true };
      }

      // If no phone number or SMS not opted in, don't load reminders
      if (!profile.phone_number || !profile.sms_opt_in) {
        // Clear any existing reminder settings in state
        setReminderSettings({
          tool1: null,
          tool2: null,
          tool3: null,
        });
        return { found: false, hasAnyInteraction: false, allNull: true };
      }

      // Get the most recent reminder for each tool (regardless of status)
      // Include cancelled_at to detect batch vs individual cancellations
      const { data: reminders, error } = await supabase
        .from('scheduled_reminders')
        .select('tool_number, lead_minutes, status, cancelled_at')
        .eq('user_id', user.id)
        .eq('horse_id', horse.id)
        .in('tool_number', [1, 2, 3])
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Failed to load reminder settings:', error);
        return { found: false, hasAnyInteraction: false, allNull: true };
      }

      // Track which tools have any interaction (even cancelled)
      const toolsWithInteraction = new Set();
      const settingsByTool = {};
      const cancelledTools = new Set();

      if (reminders && reminders.length > 0) {
        // Group by tool_number and get the most recent one for each tool
        reminders.forEach((reminder) => {
          if (!settingsByTool[reminder.tool_number]) {
            settingsByTool[reminder.tool_number] = {
              lead_minutes: reminder.lead_minutes,
              status: reminder.status,
              cancelled_at: reminder.cancelled_at
            };
            toolsWithInteraction.add(reminder.tool_number);

            // If most recent record is cancelled, track it
            if (reminder.status === 'cancelled') {
              cancelledTools.add(reminder.tool_number);
            }
          }
        });
      }

      // Detect batch cancellation (master toggle OFF) vs individual cancellation (explicit OFF)
      // IMPORTANT:
      // - If the master is OFF when we load (reminderEnabled === false), we assume cancellations came
      //   from the master toggle being turned OFF and we will restore previous times when master turns ON.
      // - If the master is ON when we load (reminderEnabled === true), any cancelled reminder is treated
      //   as an explicit OFF for that tool and will stay OFF (null) until the user sets a new time.
      const individuallyCancelledTools = new Set();
      if (cancelledTools.size > 0 && reminderEnabled) {
        // Master is ON at load: any cancelled reminder is considered explicitly turned OFF
        cancelledTools.forEach((toolNum) => {
          individuallyCancelledTools.add(toolNum);
        });
      }

      // Compute effective loaded state (when prev is all null) to know if we should turn off master on page load
      // Note: Individually cancelled reminders (while master ON) restore as null (explicit OFF),
      // while cancellations that occurred with master OFF restore lead_minutes.
      const loadedTool1 = toolsWithInteraction.has(1)
        ? (individuallyCancelledTools.has(1) ? null : (settingsByTool[1]?.lead_minutes ?? null))
        : null;
      const loadedTool2 = toolsWithInteraction.has(2)
        ? (individuallyCancelledTools.has(2) ? null : (settingsByTool[2]?.lead_minutes ?? null))
        : null;
      const loadedTool3 = toolsWithInteraction.has(3)
        ? (individuallyCancelledTools.has(3) ? null : (settingsByTool[3]?.lead_minutes ?? null))
        : null;
      const allNull = loadedTool1 == null && loadedTool2 == null && loadedTool3 == null;

      // Update reminder settings with loaded values
      setReminderSettings((prev) => {
        const next = { ...prev };
        let changed = false;

        [1, 2, 3].forEach((num) => {
          const key = `tool${num}`;

          if (toolsWithInteraction.has(num)) {
            // This tool has been interacted with before
            const saved = settingsByTool[num];
            if (cancelledTools.has(num)) {
              if (individuallyCancelledTools.has(num)) {
                // Explicitly turned OFF individually - restore as null
                if (next[key] !== null) {
                  next[key] = null;
                  changed = true;
                }
              } else {
                // Batch cancellation (master toggle turned off) - restore lead_minutes to preserve preference
                if (saved && saved.lead_minutes !== undefined && next[key] !== saved.lead_minutes) {
                  next[key] = saved.lead_minutes;
                  changed = true;
                } else if (!saved || saved.lead_minutes === undefined) {
                  // No saved value - restore as null
                  if (next[key] !== null) {
                    next[key] = null;
                    changed = true;
                  }
                }
              }
            } else {
              // Most recent action was setting a value - restore that value
              if (saved && saved.lead_minutes !== undefined && next[key] !== saved.lead_minutes) {
                next[key] = saved.lead_minutes;
                changed = true;
              }
            }
          }
          // If tool has NO interaction history, leave it as-is (null)
          // The default-to-15 effect will handle new horses
        });

        return changed ? next : prev;
      });

      // Return whether we found any reminders (active or cancelled) and if loaded state is all null
      return {
        found: reminders && reminders.length > 0,
        hasAnyInteraction: toolsWithInteraction.size > 0,
        allNull,
      };
    } catch (err) {
      console.error('Error loading reminder settings:', err);
      return { found: false, hasAnyInteraction: false, allNull: true };
    }
  }, [user?.id, horse?.id, reminderEnabled]);

  // When master toggle is ON, default any unset therapeutic reminders to 15 minutes
  // BUT ONLY if this is a NEW horse (no previous reminders in DB) AND hasn't been checked before
  // This prevents auto-enabling reminders when SMS is re-enabled
  useEffect(() => {
    if (!reminderEnabled || isRaceInPast || allTherapeuticTimesPassed || !hasRaceDate || !effectiveTimezone || !horse?.id) return;

    // Only run defaults check once per horse, not when reminderEnabled changes
    // This prevents auto-enabling when SMS is re-enabled
    if (defaultsCheckedForHorseRef.current.has(horse.id)) return;

    // Check if this horse has previous reminders in DB
    const checkAndSetDefaults = async () => {
      const result = await loadReminderSettingsFromDB();

      // Mark that we've checked for this horse (regardless of result)
      // This ensures we don't run defaults again when SMS is re-enabled
      defaultsCheckedForHorseRef.current.add(horse.id);

      // If ANY tool has been interacted with before, don't override with defaults
      // This preserves explicitly OFF reminders (null) and previous values
      if (result?.hasAnyInteraction) {
        return;
      }

      // CRITICAL FIX: Wait a bit to ensure loadReminderSettingsFromDB state updates are applied
      // This prevents race condition where defaults override loaded values (e.g., 30 mins -> 15 mins)
      await new Promise(resolve => setTimeout(resolve, 100));

      // Only set defaults if NO previous interaction exists (truly new horse)
      // Helper to get reminder date (inlined to avoid dependency issues)
      const calcReminderDate = (therapeuticTime, leadMinutes) => {
        if (!therapeuticTime || therapeuticTime === 'N/A' || !effectiveTimezone) return null;
        try {
          const [timePart, period] = therapeuticTime.split(' ');
          const [h, m] = timePart.split(':').map(Number);

          let hour24 = h;
          if (period === 'PM' && hour24 !== 12) hour24 += 12;
          if (period === 'AM' && hour24 === 12) hour24 = 0;

          const dateStr = raceDateStr ?? new Date().toISOString().slice(0, 10);
          const localDateTime = `${dateStr} ${hour24.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:00`;
          const utcDate = fromZonedTime(localDateTime, effectiveTimezone);
          utcDate.setMinutes(utcDate.getMinutes() - leadMinutes);
          return utcDate;
        } catch (err) {
          return null;
        }
      };

      const checkLeadValid = (therapeuticTime, leadMinutes) => {
        const reminderDate = calcReminderDate(therapeuticTime, leadMinutes);
        if (!reminderDate) return false;
        return reminderDate > new Date();
      };

      // CRITICAL FIX: Use functional update to check current state
      // This ensures we don't override values that were just loaded from DB (like 30 mins)
      setReminderSettings((prev) => {
        let changed = false;
        const next = { ...prev };

        [1, 2, 3].forEach((num) => {
          const key = `tool${num}`;
          const time = horse?.[`time_to_give_tool${num}`] || 'N/A';
          // Only set default to 15 minutes if:
          // 1. Therapeutic time exists
          // 2. Current value is null (not already set - this ensures we don't override loaded values)
          // 3. 15 minutes is valid
          // This ensures we don't override values loaded from DB (like 30 mins)
          if (time !== 'N/A' && next[key] == null && checkLeadValid(time, 15)) {
            next[key] = 15;
            changed = true;
          }
        });

        return changed ? next : prev;
      });
    };

    checkAndSetDefaults();
  }, [reminderEnabled, horse, isRaceInPast, allTherapeuticTimesPassed, hasRaceDate, raceDateStr, effectiveTimezone, loadReminderSettingsFromDB]);

  // Helper: build a Date for when the reminder should actually fire (UTC)
  // Uses timezone-aware conversion to handle DST and travel correctly
  const getReminderDateWithTimezone = useCallback((therapeuticTime, leadMinutes, timezone) => {
    if (!therapeuticTime || therapeuticTime === 'N/A' || !timezone) return null;
    try {
      const [timePart, period] = therapeuticTime.split(' ');
      const [h, m] = timePart.split(':').map(Number);

      let hour24 = h;
      if (period === 'PM' && hour24 !== 12) hour24 += 12;
      if (period === 'AM' && hour24 === 12) hour24 = 0;

      const dateStr = raceDateStr ?? new Date().toISOString().slice(0, 10);

      // Build local datetime string in USER timezone
      const localDateTime = `${dateStr} ${hour24
        .toString()
        .padStart(2, '0')}:${m.toString().padStart(2, '0')}:00`;

      // Convert from user's timezone → UTC
      const utcDate = fromZonedTime(localDateTime, timezone);

      // Subtract lead time in UTC (safe)
      utcDate.setMinutes(utcDate.getMinutes() - leadMinutes);

      return utcDate;
    } catch (err) {
      console.error('Error in getReminderDateWithTimezone:', err);
      return null;
    }
  }, [raceDateStr]);

  /**
   * Schedule a reminder in the database (to be processed by pg_cron)
   */
  const scheduleReminderInDB = useCallback(
    async (toolNum, therapeuticTime, leadMinutes) => {


      if (!user?.id || !horse?.id) {
        console.warn('❌ Missing user or horse ID for scheduling');
        return { success: false, error: 'Missing user or horse ID' };
      }

      try {
        // Get user's phone number from profile
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('phone_number, sms_opt_in, sms_opt_out_source')
          .eq('user_id', user.id)
          .single();

        if (profileError || !profile) {
          console.error('❌ Failed to load profile:', profileError);
          return { success: false, error: 'Failed to load profile' };
        }


        // Check for STOP opt-out first
        const isStopOptedOut = profile.sms_opt_out_source === 'stop' && !profile.sms_opt_in;
        if (isStopOptedOut) {
          return { success: false, error: 'User has opted out via STOP. Text START to re-subscribe.' };
        }

        if (!profile.sms_opt_in) {
          return { success: false, error: 'SMS not opted in' };
        }

        if (!profile.phone_number) {
          return { success: false, error: 'No phone number on profile' };
        }

        // Calculate when the reminder should be sent (UTC)
        if (!effectiveTimezone) {
          return { success: false, error: 'Timezone not loaded' };
        }

        const scheduledFor = getReminderDateWithTimezone(therapeuticTime, leadMinutes, effectiveTimezone);
        if (!scheduledFor) {
          return { success: false, error: 'Could not calculate reminder time' };
        }


        const horseName = horse?.horse_name || 'Your horse';
        const estimatedRunTime = horse?.estimated_run_time;
        const hasEstimatedRun = estimatedRunTime && estimatedRunTime !== 'N/A';
        const drawNum = horse?.draw_number != null && horse?.draw_number !== '' ? String(horse.draw_number) : null;

        // Format race date as mm/dd/yy (e.g. 02/17/26)
        let formattedDate = '';
        if (horse?.race_date) {
          const d = String(horse.race_date).trim();
          if (d.includes('-')) {
            const [y, m, day] = d.split('-');
            const year = y.length === 4 ? y.slice(-2) : y;
            formattedDate = `${m}/${day}/${year}`;
          } else {
            formattedDate = d;
          }
        }

        // Line 1: Pre Go Prep: Horse name - App 1 in X min (00:00 AM). Date: mm/dd/yy
        const headerLine = formattedDate
          ? `Pre Go Prep: ${horseName} - App ${toolNum} in ${leadMinutes} min (${therapeuticTime}). Date: ${formattedDate}`
          : `Pre Go Prep: ${horseName} - App ${toolNum} in ${leadMinutes} min (${therapeuticTime}).`;

        // Line 2: Est run 00:00 AM | Draw xx
        let runLine = '';
        if (hasEstimatedRun && drawNum != null) {
          runLine = `Est run ${estimatedRunTime} | Draw ${drawNum}`;
        } else if (hasEstimatedRun) {
          runLine = `Est run ${estimatedRunTime}`;
        } else if (drawNum != null) {
          runLine = `Draw ${drawNum}`;
        }

        const footerLine = 'Text HELP for help, STOP to stop, START to start';

        const messageParts = [headerLine];
        if (runLine) {
          messageParts.push('');
          messageParts.push(runLine);
          messageParts.push('');

        }
        messageParts.push(footerLine);
        const messageBody = messageParts.join('\n');

        // Upsert the reminder (update if exists, insert if not)
        const { data, error } = await supabase
          .from('scheduled_reminders')
          .upsert({
            user_id: user.id,
            horse_id: horse.id,
            phone_number: profile.phone_number,
            tool_number: toolNum,
            scheduled_for: scheduledFor.toISOString(),
            lead_minutes: leadMinutes,
            message_body: messageBody,
            reminder_sent: false,
            status: 'pending',
            updated_at: new Date().toISOString(),
          }, {
            onConflict: 'horse_id,tool_number',
          })
          .select()
          .single();

        if (error) {
          console.error('❌ Failed to schedule reminder (database error):', error);
          console.error('  - Error code:', error.code);
          console.error('  - Error message:', error.message);
          console.error('  - Error details:', error.details);
          return { success: false, error: error.message };
        }

        return { success: true, data };

      } catch (err) {
        console.error('❌ Error scheduling reminder (exception):', err);
        return { success: false, error: err.message };
      }
    },
    [
      user?.id,
      horse?.id,
      horse?.horse_name,
      horse?.race_date,
      horse?.estimated_run_time,
      horse?.draw_number,
      effectiveTimezone,
      getReminderDateWithTimezone,
    ]
  );

  const getHorseName = () => horse?.horse_name || 'Unnamed Horse';
  const getRiderName = () => horse?.rider_name || 'N/A';
  const getClassName = () => horse?.class_name || 'N/A';
  const getDrawNumber = () => {
    const draw = horse?.draw_number || 'N/A';
    const total = horse?.total_runs;
    if (horse?.reverse_order && total && typeof draw === 'number') {
      return `${total - draw} (Draw #${draw} Reversed)`;
    }
    return draw;
  };

  const getRaceTime = () => {
    const time = horse?.race_start_time || 'N/A';
    if (time === 'N/A') return time;
    try {
      const [hours, minutes] = time.split(':');
      const date = new Date();
      date.setHours(parseInt(hours, 10));
      date.setMinutes(parseInt(minutes, 10));
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return time;
    }
  };

  const getEstimatedRunTime = () => horse?.estimated_run_time || 'N/A';

  const getTherapeuticTime = useCallback((num) => {
    const time = horse?.[`time_to_give_tool${num}`];
    return time || 'N/A';
  }, [horse]);

  const getTherapeuticLabel = (num) => {
    const labels = {
      1: 'Therapeutic Admin Time #1',
      2: 'Therapeutic Admin Time #2',
      3: 'Therapeutic Admin Time #3',
    };
    return labels[num] || `Therapeutic Admin Time #${num}`;
  };

  // Auto-schedule reminders in the DB whenever a valid lead time is set
  // (either default 15 minutes or user-updated), as long as the race and
  // therapeutic times are still in the future.
  useEffect(() => {
    if (!reminderEnabled || isRaceInPast || allTherapeuticTimesPassed || !hasRaceDate) {
      return;
    }

    // Only attempt auto-scheduling once timezone is known
    if (!effectiveTimezone) return;

    [1, 2, 3].forEach((num) => {
      const toolKey = `tool${num}`;
      const leadMinutes = reminderSettings[toolKey];

      // Skip if no lead time set for this tool
      if (leadMinutes == null) return;

      const therapeuticTime = getTherapeuticTime(num);
      if (!therapeuticTime || therapeuticTime === 'N/A') return;

      // Ensure the reminder would still be in the future
      if (!isLeadOptionValid(therapeuticTime, leadMinutes)) return;

      // Upsert reminder in the database; safe to call multiple times per tool
      scheduleReminderInDB(num, therapeuticTime, leadMinutes).catch((err) => {
        console.error('Auto-schedule exception for tool', num, err);
      });
    });
  }, [
    reminderEnabled,
    reminderSettings,
    isRaceInPast,
    allTherapeuticTimesPassed,
    hasRaceDate,
    effectiveTimezone,
    scheduleReminderInDB,
    getTherapeuticTime,
    isLeadOptionValid,
  ]);

  // Which therapeutic application is expanded (null = all collapsed)
  const [expandedTool, setExpandedTool] = useState(null);

  // Temporary selection in reminder modal (not saved until "Set Reminder" clicked)
  const [tempReminderSelection, setTempReminderSelection] = useState(null);

  // Modal message state (for showing "already off" etc.)
  const [modalMessage, setModalMessage] = useState('');

  // Toast/Snackbar state
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Instant custom tooltips
  const [showMasterTooltip, setShowMasterTooltip] = useState(false);
  const [showMenuTooltip, setShowMenuTooltip] = useState(false);
  const [showDeleteTooltip, setShowDeleteTooltip] = useState(false);
  const [showSummaryTooltip, setShowSummaryTooltip] = useState(false);

  useEffect(() => {
    if (!showDropdown) return;

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showDropdown]);

  // Track previous reminderEnabled value to detect when it's turned OFF
  const prevReminderEnabled = useRef(reminderEnabled);

  // Track which horses have had defaults checked (to prevent auto-enabling when SMS is re-enabled)
  const defaultsCheckedForHorseRef = useRef(new Set());

  // Track previous reminder settings to detect when all reminders are turned OFF
  const prevReminderSettingsRef = useRef(reminderSettings);
  // Track when we've loaded reminder settings from DB (so we don't auto-turn-off before load completes)
  const hasReminderSettingsLoadedRef = useRef(false);

  // Auto-load timezone on mount (detect from browser if not stored)
  useEffect(() => {
    const loadUserTimezone = async () => {
      if (!user?.id) return;

      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('timezone')
          .eq('user_id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
          throw error;
        }

        // Always trust the browser's current timezone over whatever was last
        // stored. The stored value previously only got written once (when
        // empty), so it went stale after travel (e.g. PST -> MST) and caused
        // reminders to keep scheduling against the old zone's UTC offset.
        const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setUserTimezone(browserTz);

        // Keep the stored profile timezone in sync with the browser's current
        // zone (covers both first-time save and later zone changes).
        if (browserTz && profile?.timezone !== browserTz) {
          const { error: updateError } = await supabase
            .from('profiles')
            .update({ timezone: browserTz })
            .eq('user_id', user.id);

          if (updateError) {
            console.error('❌ Failed to save timezone:', updateError);
          }
        }
      } catch (err) {
        console.error('Failed to load timezone:', err);
        // Fallback to browser timezone
        const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setUserTimezone(browserTz);
      }
    };

    loadUserTimezone();
  }, [user?.id]);

  // Update current time every minute to trigger re-renders for time-based checks
  // This ensures reminder status updates automatically when time passes without page refresh
  useEffect(() => {
    // Update immediately on mount
    setCurrentTime(new Date());

    // Then update every minute
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // 60 seconds = 1 minute

    return () => clearInterval(interval);
  }, []);

  // Load SMS opt-out source and global SMS status on mount (to check for STOP opt-out and SMS enabled)
  useEffect(() => {
    if (user?.id) {
      const loadSmsStatus = async () => {
        try {
          const { data: profile } = await supabase
            .from('profiles')
            .select('sms_opt_out_source, phone_number, sms_opt_in')
            .eq('user_id', user.id)
            .single();

          if (profile) {
            setSmsOptOutSource((profile?.sms_opt_out_source || '').toString().toLowerCase());
            // Track phone number and SMS opt-in separately for better tooltip messages
            const hasPhone = !!profile.phone_number;
            const hasOptIn = !!profile.sms_opt_in;
            setHasPhoneNumber(hasPhone);
            setHasSmsOptIn(hasOptIn);
            // Check if SMS is enabled globally (has phone number and sms_opt_in is true)
            const isSmsEnabled = hasPhone && hasOptIn;
            setSmsEnabledGlobally(isSmsEnabled);
          }
        } catch (err) {
          console.error('Error loading SMS status:', err);
        }
      };
      loadSmsStatus();
    }
  }, [user?.id]);

  // Load reminder settings from DB on mount if reminder is enabled
  useEffect(() => {
    if (reminderEnabled && user?.id && horse?.id) {
      // First verify phone number before loading
      const verifyAndLoad = async () => {
        try {
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('phone_number, sms_opt_in, sms_opt_out_source')
            .eq('user_id', user.id)
            .single();

          if (profileError || !profile) {
            console.warn('Profile check failed - not loading reminders');
            return;
          }

          // Store opt-out source and global SMS status
          setSmsOptOutSource((profile?.sms_opt_out_source || '').toString().toLowerCase());
          // Track phone number and SMS opt-in separately for better tooltip messages
          const hasPhone = !!profile.phone_number;
          const hasOptIn = !!profile.sms_opt_in;
          setHasPhoneNumber(hasPhone);
          setHasSmsOptIn(hasOptIn);
          const isSmsEnabled = hasPhone && hasOptIn;
          setSmsEnabledGlobally(isSmsEnabled);

          if (!profile.phone_number || !profile.sms_opt_in) {
            // Clear reminder settings if phone number is missing
            setReminderSettings({
              tool1: null,
              tool2: null,
              tool3: null,
            });
            return;
          }

          // Always try to load previous settings first
          // This will return { found: true, hasAnyInteraction: true } if previous reminders exist
          // The default-to-15 effect will check this and skip if hasAnyInteraction=true
          const result = await loadReminderSettingsFromDB();
          hasReminderSettingsLoadedRef.current = true;
          // Page load with master ON but no reminders: turn off master once so UI matches state
          if (result.allNull && reminderEnabled && onToggleReminder) {
            onToggleReminder();
          }
        } catch (err) {
          console.error('Error verifying phone number:', err);
        }
      };

      verifyAndLoad();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Safely format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const [year, month, day] = dateString.split('-');
    return `${month}/${day}/${year}`;
  };

  // Wrapper for backward compatibility (uses effectiveTimezone)
  // Must be defined after getReminderDateWithTimezone
  const getReminderDate = useCallback((therapeuticTime, leadMinutes) => {
    return getReminderDateWithTimezone(therapeuticTime, leadMinutes, effectiveTimezone);
  }, [getReminderDateWithTimezone, effectiveTimezone]);

  // Calculate reminder time based on therapeutic time (e.g. "12:30 PM") and lead minutes
  const calculateReminderTime = (therapeuticTime, leadMinutes) => {
    const date = getReminderDate(therapeuticTime, leadMinutes);
    if (!date) return null;
    try {
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: effectiveTimezone,
      });
    } catch {
      return null;
    }
  };

  // Toast helpers
  const showToast = (message) => {
    setToastMessage(message);
    setToastOpen(true);
  };

  const handleToastClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setToastOpen(false);
    setTimeout(() => setToastMessage(''), 300);
  };

  // ============================================================================
  // DATABASE SCHEDULING FUNCTIONS
  // ============================================================================

  /**
   * Cancel a reminder in the database
   */
  const cancelReminderInDB = useCallback(async (toolNum) => {
    if (!user?.id || !horse?.id) {
      console.warn('Missing user or horse ID for cancellation');
      return { success: false, error: 'Missing user or horse ID' };
    }

    try {
      const { error } = await supabase
        .from('scheduled_reminders')
        .update({
          status: 'cancelled',
          cancelled_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id)
        .eq('horse_id', horse.id)
        .eq('tool_number', toolNum)
        .in('status', ['pending', 'processing']);

      if (error) {
        console.error('Failed to cancel reminder:', error);
        return { success: false, error: error.message };
      }

      return { success: true };

    } catch (err) {
      console.error('Error cancelling reminder:', err);
      return { success: false, error: err.message };
    }
  }, [user?.id, horse?.id]);

  /**
   * Cancel ALL reminders for this horse (when master toggle is turned off)
   */
  const cancelAllRemindersInDB = useCallback(async () => {
    if (!user?.id || !horse?.id) {
      return { success: false, error: 'Missing user or horse ID' };
    }

    try {
      const { error } = await supabase
        .from('scheduled_reminders')
        .update({
          status: 'cancelled',
          cancelled_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id)
        .eq('horse_id', horse.id)
        .in('status', ['pending', 'processing']);

      if (error) {
        console.error('Failed to cancel all reminders:', error);
        return { success: false, error: error.message };
      }

      return { success: true };

    } catch (err) {
      console.error('Error cancelling all reminders:', err);
      return { success: false, error: err.message };
    }
  }, [user?.id, horse?.id]);

  // Effect to cancel all reminders when master toggle is turned OFF
  // and load previous settings when toggle is turned back ON
  useEffect(() => {
    // If master toggle was ON and is now OFF, cancel all reminders for this horse
    if (prevReminderEnabled.current === true && reminderEnabled === false) {
      cancelAllRemindersInDB();
    }

    // If master toggle was OFF and is now ON, load previous reminder settings from DB
    if (prevReminderEnabled.current === false && reminderEnabled === true) {

      const hasPreservedValues =
        reminderSettings.tool1 != null ||
        reminderSettings.tool2 != null ||
        reminderSettings.tool3 != null;

      if (hasPreservedValues) {
        //ignore
      } else {
        loadReminderSettingsFromDB().then(() => {
          hasReminderSettingsLoadedRef.current = true;
        });
      }
    }

    prevReminderEnabled.current = reminderEnabled;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reminderEnabled, cancelAllRemindersInDB, loadReminderSettingsFromDB, horse?.id]);

  // Effect to automatically turn OFF master toggle when user had at least one reminder on and turned all off
  // Does NOT turn off when user just turned master ON (so they can set reminders)
  useEffect(() => {
    if (!reminderEnabled || !hasTherapeuticTimes || isRaceInPast || allTherapeuticTimesPassed || !hasRaceDate || !onToggleReminder) return;

    const allRemindersOff =
      reminderSettings.tool1 === null &&
      reminderSettings.tool2 === null &&
      reminderSettings.tool3 === null;

    if (!allRemindersOff) {
      prevReminderSettingsRef.current = reminderSettings;
      return;
    }

    // Only auto-turn-off after we've loaded from DB (avoids turning off before load completes)
    if (!hasReminderSettingsLoadedRef.current) {
      prevReminderSettingsRef.current = reminderSettings;
      return;
    }

    // Only turn off when user had at least one reminder on and then turned all off (not when they just turned master ON)
    const prevHadReminder =
      prevReminderSettingsRef.current.tool1 != null ||
      prevReminderSettingsRef.current.tool2 != null ||
      prevReminderSettingsRef.current.tool3 != null;

    if (!prevHadReminder) {
      prevReminderSettingsRef.current = reminderSettings;
      return;
    }

    prevReminderSettingsRef.current = reminderSettings;
    setTimeout(() => {
      if (onToggleReminder) onToggleReminder();
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reminderSettings.tool1, reminderSettings.tool2, reminderSettings.tool3, reminderEnabled, hasTherapeuticTimes, isRaceInPast, allTherapeuticTimesPassed, hasRaceDate, onToggleReminder, horse?.id]);

  // Effect to automatically turn OFF master toggle when SMS is disabled globally
  useEffect(() => {
    // If SMS is disabled globally and toggle is ON, turn it OFF
    if (!smsEnabledGlobally && reminderEnabled && onToggleReminder) {
      setTimeout(() => {
        if (onToggleReminder) {
          onToggleReminder();
        }
      }, 0);
    }
  }, [smsEnabledGlobally, reminderEnabled, onToggleReminder, horse?.id]);

  // ============================================================================

  // Reminder change
  const handleReminderChange = async (toolNum, value, therapeuticTime) => {
    // Only allow changing per-tool reminders when master toggle is ON
    if (!reminderEnabled || isRaceInPast || allTherapeuticTimesPassed || !hasRaceDate) return;
    const newLeadTime = value === 'off' ? null : parseInt(value, 10);

    // If turning ON / changing lead time, prevent impossible "past" reminders
    if (newLeadTime !== null && therapeuticTime && therapeuticTime !== 'N/A') {
      const reminderDate = getReminderDate(therapeuticTime, newLeadTime);
      if (!reminderDate || reminderDate <= new Date()) {
        showToast(
          "It's too late to set this reminder. Choose a smaller time or adjust the therapeutic time."
        );
        return;
      }
    }

    // If turning OFF, cancel the reminder in the database
    if (newLeadTime === null) {
      setReminderLoading(true);
      const result = await cancelReminderInDB(toolNum);
      setReminderLoading(false);

      if (!result.success) {
        console.warn('Failed to cancel reminder in DB:', result.error);
        // Still update local state even if DB cancel fails
      }
    }

    setReminderSettings((prev) => ({
      ...prev,
      [`tool${toolNum}`]: newLeadTime,
    }));

    let toastMsg = newLeadTime === null ? 'Reminder turned off' : 'Text reminder updated';
    if (newLeadTime !== null && therapeuticTime) {
      const calculatedTime = calculateReminderTime(therapeuticTime, newLeadTime);
      if (calculatedTime) {
        toastMsg = `Text reminder updated. You'll receive a text at ${calculatedTime}`;
      }
    }
    showToast(toastMsg);
  };

  // Save reminder to database
  const handleSaveReminder = async (toolNum, therapeuticTime, reminderLeadTime) => {
    // Do nothing if master toggle is OFF, race in past, all times passed, or race date missing
    if (!reminderEnabled || isRaceInPast || allTherapeuticTimesPassed || !hasRaceDate) {
      return;
    }


    setReminderLoading(true);

    // Schedule the reminder in the database
    const result = await scheduleReminderInDB(toolNum, therapeuticTime, reminderLeadTime);

    setReminderLoading(false);

    if (result.success) {
      let toastMsg = '✅ Reminder scheduled!';
      if (reminderLeadTime !== null && therapeuticTime) {
        const calculatedTime = calculateReminderTime(therapeuticTime, reminderLeadTime);
        if (calculatedTime) {
          toastMsg = `Reminder scheduled! You'll receive a text at ${calculatedTime}`;
        }
      }
      showToast(toastMsg);
    } else {
      // Show error but still update local state
      console.error('❌ Failed to schedule reminder:', result.error);
      showToast(`⚠️ Couldn't schedule reminder: ${result.error || 'Unknown error'}`);
    }
  };

  return (
    <div
      style={{
        backgroundColor: colors?.surface || '#ffffff',
        border: `1px solid ${colors?.border || '#e2e8f0'}`,
        borderRadius: '8px',
        padding: '16px',
        transition: 'all 0.2s ease',
        position: 'relative',
        overflow: 'visible',
      }}
    >
      {/* Header */}
      <div className="horse-card-header">
        <div className="horse-card-header__left">
          {/* Horse avatar with rounded square background */}
          <div className="horse-card-header__avatar">
            🐴
          </div>
          <div>
            <h4 className="horse-card-header__name">
              {getHorseName()}
            </h4>
            <p className="horse-card-header__rider">
              Rider: {getRiderName()}
            </p>
          </div>
        </div>
        <div ref={menuRef} className="horse-card-header__actions">
          {/* Text Reminders - Compact toggle matching button sizes */}
          {(() => {
            // Check if user has STOP opt-out
            const isStopOptedOut = smsOptOutSource === 'stop';

            // Calculate shouldShowOn before using it in styles
            const canUseReminders = !isRaceInPast && !allTherapeuticTimesPassed && hasRaceDate && hasTherapeuticTimes;

            // Master toggle should be:
            // - OFF if STOP opt-out
            // - OFF if SMS is disabled globally (no phone number or sms_opt_in is false)
            // - OFF if no therapeutic apps exist
            // - OFF if reminders can't be used (race past, etc.)
            // - ON if reminderEnabled=true AND SMS is enabled globally (allows setting reminders even if none are set yet)
            // BUT: If all reminders are turned OFF, toggle will be automatically turned OFF
            const shouldShowOn = !isStopOptedOut && smsEnabledGlobally && canUseReminders && hasTherapeuticTimes && reminderEnabled;

            // Free users keep the toggle clickable so the upgrade modal can fire — the parent
            // handler short-circuits to the upgrade prompt before touching any DB state.
            const baseDisabled = isStopOptedOut || !hasTherapeuticTimes || isRaceInPast || allTherapeuticTimesPassed || !hasRaceDate;
            const shouldDisableToggle = baseDisabled && isSubActive;
            const boxClickHandler =
              (canUseReminders || !isSubActive) && onToggleReminder ? onToggleReminder : undefined;

            return (
              <div
                className="horse-card-toggle-box"
                onMouseEnter={() => setShowMasterTooltip(true)}
                onMouseLeave={() => setShowMasterTooltip(false)}
                onClick={boxClickHandler}
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: shouldShowOn ? '#f0fdf4' : '#ffffff',
                  padding: '0 12px',
                  borderRadius: '8px',
                  border: shouldShowOn ? '1px solid #86efac' : '1px solid #e2e8f0',
                  transition: 'all 0.2s ease',
                  height: '42px',
                  boxSizing: 'border-box',
                  cursor: boxClickHandler ? 'pointer' : 'default',
                }}
              >
                {/* Icon - alarm clock with/without slash */}
                <span style={{ fontSize: '1rem', lineHeight: 1, position: 'relative', display: 'inline-block' }}>
                  ⏰
                  {!shouldShowOn && (
                    <span style={{
                      position: 'absolute',
                      top: '50%',
                      left: '-1px',
                      width: '18px',
                      height: '2px',
                      backgroundColor: '#dc2626',
                      transform: 'rotate(-45deg)',
                      transformOrigin: 'center',
                    }} />
                  )}
                </span>
                <span
                  className="horse-card-toggle-label-text"
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: colors?.text?.secondary || '#64748b',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Text Reminder
                </span>

                {/* Toggle (visual only — click handled by the surrounding box) */}
                <button
                  type="button"
                  disabled={shouldDisableToggle || !onToggleReminder}
                  tabIndex={-1}
                  className={`horse-card-toggle ${shouldShowOn ? 'horse-card-toggle--on' : 'horse-card-toggle--off'} ${shouldDisableToggle ? 'horse-card-toggle--disabled' : ''}`}
                  style={{
                    cursor: 'inherit',
                    pointerEvents: 'none',
                  }}
                >
                  <div
                    className={`horse-card-toggle__knob ${shouldShowOn ? 'horse-card-toggle__knob--on' : 'horse-card-toggle__knob--off'}`}
                  />
                </button>

                {/* ON/OFF text - Always show OFF if time has passed, even if reminderEnabled is true */}
                <span
                  style={{
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    color: shouldShowOn ? '#16a34a' : '#94a3b8',
                  }}
                >
                  {shouldShowOn ? 'ON' : 'OFF'}
                </span>

                {showMasterTooltip && (
                  <div className="horse-card-tooltip">
                    {!hasPhoneNumber
                      ? 'Add your phone number on Profile to enable reminders.'
                      : !isSubActive
                      ? 'Text reminders are a premium feature. Subscribe to enable.'
                      : isStopOptedOut
                      ? 'You unsubscribed via STOP. Text START to re-subscribe.'
                      : !smsEnabledGlobally
                        ? 'SMS is disabled. Enable SMS in Profile settings to use reminders.'
                        : isRaceInPast
                          ? 'Race time has passed'
                          : !hasTherapeuticTimes
                            ? 'No therapeutic schedule - can\'t set reminders'
                            : allTherapeuticTimesPassed
                              ? 'All reminder times have passed'
                              : !hasRaceDate
                                ? 'Add a race date first'
                                : shouldShowOn
                                  ? 'Text reminders ON - Click to turn OFF'
                                  : 'Text reminders OFF - Click to turn ON'}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Send summary icon next to toggle */}
          {onSendSummary && !isRaceInPast && !isRaceTimePassed && smsOptOutSource !== 'stop' && (
            <div
              onClick={() => onSendSummary(horse)}
              onMouseEnter={() => setShowSummaryTooltip(true)}
              onMouseLeave={() => setShowSummaryTooltip(false)}
              style={{ position: 'relative', display: 'inline-flex' }}
            >
              <button
                type="button"
                disabled={false}
                className={`horse-card-btn horse-card-btn--primary ${!smsEnabledGlobally ? 'horse-card-btn--disabled' : ''}`}
                aria-label="Send summary text"
                style={{
                  cursor: smsEnabledGlobally ? 'pointer' : 'not-allowed',
                  opacity: smsEnabledGlobally ? 1 : 0.6,
                }}
              >
                <span className="horse-card-btn__icon">📱</span>
                <span className="horse-card-btn__text--long">Send Summary Text</span>
                <span className="horse-card-btn__text--short">Send Summary</span>
              </button>
              {showSummaryTooltip && (
                <div className="horse-card-tooltip">
                  {!hasPhoneNumber
                    ? 'Add your phone number on Profile to send summary text'
                    : !isSubActive
                    ? 'Premium feature — subscribe to send summary text'
                    : !smsEnabledGlobally
                    ? !hasPhoneNumber
                      ? 'Add a phone number in Profile settings to send summary text'
                      : !hasSmsOptIn
                        ? 'Enable SMS notifications in Profile settings to send summary text'
                        : 'Send summary text'
                    : 'Send summary text'}
                </div>
              )}
            </div>
          )}

          {/* Menu icon with tooltip */}
          <div
            onMouseEnter={() => setShowMenuTooltip(true)}
            onMouseLeave={() => setShowMenuTooltip(false)}
            style={{ position: 'relative', display: 'inline-flex' }}
          >
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="horse-card-btn horse-card-btn--secondary"
              aria-label="More actions"
            >
              <FaEllipsisV size={12} />
              More
            </button>
            {showMenuTooltip && (
              <div className="horse-card-tooltip">
                Select here to Edit horse or Clone horse
              </div>
            )}
          </div>
          {showDropdown && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                backgroundColor: colors?.surface || '#ffffff',
                border: `1px solid ${colors?.border || '#e2e8f0'}`,
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                zIndex: 10,
                minWidth: '150px',
              }}
            >
              <button
                onClick={() => {
                  onEdit(horse);
                  setShowDropdown(false);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 16px',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: colors?.text?.primary || '#1a202c',
                  fontSize: '0.9rem',
                }}
              >
                Edit Horse
              </button>
              <button
                onClick={() => {
                  onClone(horse);
                  setShowDropdown(false);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 16px',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: colors?.text?.primary || '#1a202c',
                  fontSize: '0.9rem',
                }}
              >
                Clone Horse
              </button>
            </div>
          )}
          <div
            onMouseEnter={() => setShowDeleteTooltip(true)}
            onMouseLeave={() => setShowDeleteTooltip(false)}
            style={{ position: 'relative', display: 'inline-flex' }}
          >
            <button
              onClick={() => onDelete(horse?.id)}
              className="horse-card-btn horse-card-btn--danger"
              aria-label="Delete horse"
            >
              <FaTrash size={12} />
              Remove
            </button>
            {showDeleteTooltip && (
              <div className="horse-card-tooltip">
                Delete this saved horse
              </div>
            )}
          </div>
        </div>
      </div>

      {/* (text label moved next to header via ON/OFF pill above) */}

      {/* Horizontally scrollable info cards */}
      <div className="horse-card-info-scroll">
        {/* Race Start Time */}
        <div className="horse-card-info-card">
          <p className="horse-card-info-card__label">Race Start Time</p>
          <p className="horse-card-info-card__value">{getRaceTime()}</p>
        </div>

        {/* Race Timezone */}
        <div className="horse-card-info-card">
          <p className="horse-card-info-card__label">Timezone</p>
          <p className="horse-card-info-card__value" title={RACE_TIMEZONE_LABELS[horse?.race_timezone]?.full || 'Detected from device'}>
            {RACE_TIMEZONE_LABELS[horse?.race_timezone]?.short || 'Auto'}
          </p>
        </div>

        {/* Class Name */}
        <div className="horse-card-info-card">
          <p className="horse-card-info-card__label">Class Name</p>
          <p className="horse-card-info-card__value">{getClassName()}</p>
        </div>

        {/* Draw Position */}
        <div className="horse-card-info-card">
          <p className="horse-card-info-card__label">Draw Position</p>
          <p className="horse-card-info-card__value">{getDrawNumber()}</p>
        </div>

        {/* Race Date */}
        <div className="horse-card-info-card">
          <p className="horse-card-info-card__label">Race Date</p>
          <p className="horse-card-info-card__value">{formatDate(horse?.race_date)}</p>
        </div>

        {/* Race Name */}
        <div className="horse-card-info-card">
          <p className="horse-card-info-card__label">Race Name</p>
          <p className="horse-card-info-card__value">{horse?.race_name || 'N/A'}</p>
        </div>

        {/* Estimated Go Time */}
        <div className="horse-card-info-card">
          <p className="horse-card-info-card__label">Estimated Go Time</p>
          <p className="horse-card-info-card__value horse-card-info-card__value--success">{getEstimatedRunTime()}</p>
        </div>
      </div>

      {/* Therapeutic reminders section */}
      <div className="horse-card-therapeutic-section">
        <h3 className="horse-card-therapeutic-title">Therapeutic Admin Schedule</h3>
        <div className="horse-card-therapeutic-grid">
          {[1, 2, 3].map((num) => {
            const time = getTherapeuticTime(num);
            if (time === 'N/A') return null;

            const toolKey = `tool${num}`;
            const reminderLeadTime = reminderSettings[toolKey];
            const reminderTime =
              reminderLeadTime != null
                ? calculateReminderTime(time, reminderLeadTime)
                : null;

            // Get the actual Date object for the reminder (not just the formatted time string)
            const reminderDate = reminderLeadTime != null ? getReminderDate(time, reminderLeadTime) : null;

            // Check if reminder time has passed - compares full date and time, not just time
            // Use currentTime to ensure consistent time checks and automatic UI updates
            const reminderTimeHasPassed = reminderDate ? reminderDate <= currentTime : false;
            const isReminderEnabled = reminderLeadTime !== null && !reminderTimeHasPassed && !isRaceInPast && !allTherapeuticTimesPassed;

            return (
              <div key={num} className={`horse-card-therapeutic-card horse-card-therapeutic-card--tool${num}`}>
                <p className="horse-card-therapeutic-card__title">
                  {getTherapeuticLabel(num)}
                </p>
                <p className="horse-card-therapeutic-card__time">
                  {time}
                </p>
                <p className="horse-card-therapeutic-card__reminder-status">
                  {!reminderEnabled || reminderTimeHasPassed || isRaceInPast || allTherapeuticTimesPassed
                    ? 'Text reminder: Off'
                    : (isReminderEnabled && reminderTime ? `Reminder at ${reminderTime}` : 'Text reminder: Off')}
                </p>

                {/* Reminder button with instant tooltip */}
                <div
                  style={{ position: 'relative', width: '100%' }}
                  onMouseEnter={(e) => {
                    const tooltip = e.currentTarget.querySelector('.reminder-btn-tooltip');
                    if (tooltip) tooltip.style.display = 'block';
                  }}
                  onMouseLeave={(e) => {
                    const tooltip = e.currentTarget.querySelector('.reminder-btn-tooltip');
                    if (tooltip) tooltip.style.display = 'none';
                  }}
                >
                  <button
                    type="button"
                    className="horse-card-therapeutic-card__reminder-btn"
                    onClick={() => {
                      if (expandedTool === num) {
                        setExpandedTool(null);
                        setTempReminderSelection(null);
                      } else {
                        setExpandedTool(num);
                        setModalMessage(''); // Clear any previous message
                        // If reminder is currently off (null), show 'off' as selected
                        setTempReminderSelection(reminderLeadTime === null ? 'off' : reminderLeadTime);
                      }
                    }}
                    disabled={!reminderEnabled}
                    style={{
                      opacity: !reminderEnabled ? 0.5 : 1,
                      cursor: !reminderEnabled ? 'not-allowed' : 'pointer',
                      backgroundColor: (isReminderEnabled && reminderEnabled) ? '#f0fdf4' : undefined,
                      borderColor: (isReminderEnabled && reminderEnabled) ? '#86efac' : undefined,
                    }}
                  >
                    {(isReminderEnabled && reminderEnabled) ? (
                      <>⏰ Reminder On</>
                    ) : (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ position: 'relative', display: 'inline-block' }}>
                          ⏰
                          <span style={{
                            position: 'absolute',
                            top: '50%',
                            left: '-2px',
                            width: '20px',
                            height: '2px',
                            backgroundColor: '#dc2626',
                            transform: 'rotate(-45deg)',
                          }} />
                        </span>
                        Set Reminder
                      </span>
                    )}
                  </button>
                  {/* Instant tooltip */}
                  <div
                    className="reminder-btn-tooltip"
                    style={{
                      display: 'none',
                      position: 'absolute',
                      bottom: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      marginBottom: '6px',
                      backgroundColor: '#111827',
                      color: '#f9fafb',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      fontSize: '0.75rem',
                      boxShadow: '0 4px 10px rgba(0,0,0,0.25)',
                      whiteSpace: 'nowrap',
                      zIndex: 50,
                    }}
                  >
                    {!reminderEnabled
                      ? 'Turn on Text Reminders above first'
                      : isReminderEnabled
                        ? `Click to change reminder (currently ${reminderLeadTime} min before)`
                        : 'Click to set a text reminder'}
                  </div>
                </div>

                {/* Reminder Modal */}
                {reminderEnabled && expandedTool === num && (
                  <>
                    {/* Backdrop */}
                    <div
                      onClick={() => {
                        setModalMessage('');
                        setTempReminderSelection(null);
                        setExpandedTool(null);
                      }}
                      style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        zIndex: 999,
                      }}
                    />
                    {/* Modal */}
                    <div
                      style={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: '#ffffff',
                        borderRadius: '16px',
                        padding: '24px',
                        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                        zIndex: 1000,
                        width: 'min(360px, calc(100vw - 32px))',
                      }}
                    >
                      <h3 style={{ margin: '0 0 8px 0', fontSize: '1.2rem', fontWeight: 700, color: '#1a202c' }}>
                        Set Reminder Time
                      </h3>
                      <p style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: '#64748b' }}>
                        Receive reminder before therapeutic application time:
                      </p>
                      <p style={{
                        margin: '0 0 16px 0',
                        fontSize: '0.85rem',
                        color: isReminderEnabled ? '#16a34a' : '#94a3b8',
                        fontStyle: 'italic',
                      }}>
                        Current: {isReminderEnabled ? `${reminderLeadTime} min before` : 'Off'}
                      </p>

                      {/* Time options grid */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                        {/* Off option - full width */}
                        <button
                          type="button"
                          onClick={() => setTempReminderSelection('off')}
                          style={{
                            padding: '12px 16px',
                            borderRadius: '8px',
                            border: tempReminderSelection === 'off' ? '2px solid #7c3aed' : '1px solid #e2e8f0',
                            backgroundColor: tempReminderSelection === 'off' ? '#f3f0ff' : '#ffffff',
                            color: '#1a202c',
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            gridColumn: '1 / -1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                          }}
                        >
                          <span style={{ position: 'relative', display: 'inline-block' }}>
                            ⏰
                            <span style={{
                              position: 'absolute',
                              top: '50%',
                              left: '-2px',
                              width: '20px',
                              height: '2px',
                              backgroundColor: '#dc2626',
                              transform: 'rotate(-45deg)',
                            }} />
                          </span>
                          Off - No Reminder
                        </button>

                        {/* Show message if all time options have passed */}
                        {[5, 10, 15, 30, 60].every((mins) => !isLeadOptionValid(time, mins)) && (
                          <p style={{
                            gridColumn: '1 / -1',
                            color: '#dc2626',
                            fontSize: '0.85rem',
                            textAlign: 'center',
                            margin: '8px 0'
                          }}>
                            ⚠️ All reminder times have passed
                          </p>
                        )}

                        {/* Only show valid time options */}
                        {[
                          { value: 5, label: '5 min' },
                          { value: 10, label: '10 min' },
                          { value: 15, label: '15 min' },
                          { value: 30, label: '30 min' },
                          { value: 60, label: '1 hour' },
                        ]
                          .filter((option) => isLeadOptionValid(time, option.value))
                          .map((option) => {
                            const isSelected = tempReminderSelection === option.value;
                            return (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => setTempReminderSelection(option.value)}
                                style={{
                                  width: '100%',
                                  padding: '12px 16px',
                                  borderRadius: '8px',
                                  border: isSelected ? '2px solid #7c3aed' : '1px solid #e2e8f0',
                                  backgroundColor: isSelected ? '#f3f0ff' : '#ffffff',
                                  color: '#1a202c',
                                  fontSize: '0.9rem',
                                  fontWeight: 500,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                {option.label}
                              </button>
                            );
                          })}
                      </div>

                      {/* Message display */}
                      {modalMessage && (
                        <div style={{
                          padding: '12px 16px',
                          backgroundColor: '#fef3c7',
                          border: '1px solid #f59e0b',
                          borderRadius: '8px',
                          color: '#92400e',
                          fontSize: '0.9rem',
                          marginBottom: '16px',
                          textAlign: 'center',
                        }}>
                          {modalMessage}
                        </div>
                      )}

                      {/* Action buttons */}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                        <button
                          type="button"
                          onClick={() => {
                            setModalMessage('');
                            setTempReminderSelection(null);
                            setExpandedTool(null);
                          }}
                          style={{
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: 'transparent',
                            color: '#64748b',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (tempReminderSelection === 'off') {
                              // Check if reminder is already off
                              if (!isReminderEnabled) {
                                setModalMessage('Reminder is already off. Click Cancel to close.');
                                return; // Don't close the modal
                              } else {
                                handleReminderChange(num, 'off', time);
                                showToast('Reminder turned off');
                              }
                            } else if (tempReminderSelection) {
                              handleReminderChange(num, tempReminderSelection.toString(), time);
                              handleSaveReminder(num, time, tempReminderSelection);
                            } else {
                              console.warn('⚠️ No tempReminderSelection - button should be disabled?');
                            }
                            setModalMessage('');
                            setTempReminderSelection(null);
                            setExpandedTool(null);
                          }}
                          style={{
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: '#7c3aed',
                            color: '#ffffff',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                          }}
                        >
                          {tempReminderSelection === 'off' ? 'Turn Off' : 'Set Reminder'}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Snackbar Toast Notification */}
      <Snackbar
        open={toastOpen && Boolean(toastMessage)}
        autoHideDuration={4000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleToastClose}
          severity="success"
          variant="filled"
          sx={{
            width: '100%',
            alignItems: 'center',
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              padding: 0,
            },
            '& .MuiAlert-action': {
              alignItems: 'center',
              paddingTop: 0,
            },
          }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default HorseCard;

